// ../../../node_modules/.pnpm/@effect+sql-sqlite-wasm@0.52.0_@effect+experimental@0.60.0_@effect+platform@0.96.2_effe_0855b018046009fc0ff8592af8c6f7c3/node_modules/@effect/sql-sqlite-wasm/dist/esm/SqliteClient.js
import * as Reactivity from "@effect/experimental/Reactivity";
import * as Client from "@effect/sql/SqlClient";
import { SqlError } from "@effect/sql/SqlError";
import * as Statement from "@effect/sql/Statement";
import * as WaSqlite from "@dxos/wa-sqlite";
import SQLiteESMFactory from "@dxos/wa-sqlite/dist/wa-sqlite.mjs";
import { MemoryVFS } from "@dxos/wa-sqlite/src/examples/MemoryVFS.js";
import * as Chunk from "effect/Chunk";
import * as Config from "effect/Config";
import * as Context from "effect/Context";
import * as Deferred from "effect/Deferred";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as FiberRef from "effect/FiberRef";
import { identity } from "effect/Function";
import { globalValue } from "effect/GlobalValue";
import * as Layer from "effect/Layer";
import * as Scope from "effect/Scope";
import * as ScopedRef from "effect/ScopedRef";
import * as Stream from "effect/Stream";
var ATTR_DB_SYSTEM_NAME = "db.system.name";
var TypeId = /* @__PURE__ */ Symbol.for("@effect/sql-sqlite-wasm/SqliteClient");
var SqliteClient = /* @__PURE__ */ Context.GenericTag("@effect/sql-sqlite-wasm/SqliteClient");
var initModule = /* @__PURE__ */ Effect.runSync(/* @__PURE__ */ Effect.cached(/* @__PURE__ */ Effect.promise(() => SQLiteESMFactory())));
var initEffect = /* @__PURE__ */ Effect.runSync(/* @__PURE__ */ Effect.cached(/* @__PURE__ */ initModule.pipe(/* @__PURE__ */ Effect.map((module) => WaSqlite.Factory(module)))));
var registered = /* @__PURE__ */ globalValue("@effect/sql-sqlite-wasm/registered", () => /* @__PURE__ */ new Set());
var makeMemory = (options) => Effect.gen(function* () {
  const reactivity = yield* Reactivity.Reactivity;
  const compiler = Statement.makeCompilerSqlite(options.transformQueryNames);
  const transformRows = options.transformResultNames ? Statement.defaultTransforms(options.transformResultNames).array : void 0;
  const makeConnection = Effect.gen(function* () {
    const sqlite3 = yield* initEffect;
    if (registered.has("memory-vfs") === false) {
      registered.add("memory-vfs");
      const module = yield* initModule;
      const vfs = new MemoryVFS("memory-vfs", module);
      sqlite3.vfs_register(vfs, false);
    }
    const db = yield* Effect.acquireRelease(Effect.try({
      try: () => sqlite3.open_v2(":memory:", void 0, "memory-vfs"),
      catch: (cause) => new SqlError({
        cause,
        message: "Failed to open database"
      })
    }), (db2) => Effect.sync(() => sqlite3.close(db2)));
    if (options.installReactivityHooks) {
      sqlite3.update_hook(db, (_op, _db, table, rowid) => {
        if (!table) return;
        const id = String(Number(rowid));
        reactivity.unsafeInvalidate({
          [table]: [id]
        });
      });
    }
    const run = (sql, params = [], rowMode = "object") => Effect.try({
      try: () => {
        const results = [];
        for (const stmt of sqlite3.statements(db, sql)) {
          let columns;
          sqlite3.bind_collection(stmt, params);
          while (sqlite3.step(stmt) === WaSqlite.SQLITE_ROW) {
            columns = columns ?? sqlite3.column_names(stmt);
            const row = sqlite3.row(stmt);
            if (rowMode === "object") {
              const obj = {};
              for (let i = 0; i < columns.length; i++) {
                obj[columns[i]] = row[i];
              }
              results.push(obj);
            } else {
              results.push(row);
            }
          }
        }
        return results;
      },
      catch: (cause) => new SqlError({
        cause,
        message: "Failed to execute statement"
      })
    });
    return identity({
      execute(sql, params, transformRows2) {
        return transformRows2 ? Effect.map(run(sql, params), transformRows2) : run(sql, params);
      },
      executeRaw(sql, params) {
        return run(sql, params);
      },
      executeValues(sql, params) {
        return run(sql, params, "array");
      },
      executeUnprepared(sql, params, transformRows2) {
        return this.execute(sql, params, transformRows2);
      },
      executeStream(sql, params, transformRows2) {
        function* stream() {
          for (const stmt of sqlite3.statements(db, sql)) {
            let columns;
            sqlite3.bind_collection(stmt, params);
            while (sqlite3.step(stmt) === WaSqlite.SQLITE_ROW) {
              columns = columns ?? sqlite3.column_names(stmt);
              const row = sqlite3.row(stmt);
              const obj = {};
              for (let i = 0; i < columns.length; i++) {
                obj[columns[i]] = row[i];
              }
              yield obj;
            }
          }
        }
        return Stream.suspend(() => Stream.fromIteratorSucceed(stream()[Symbol.iterator]())).pipe(transformRows2 ? Stream.mapChunks((chunk) => Chunk.unsafeFromArray(transformRows2(Chunk.toReadonlyArray(chunk)))) : identity, Stream.mapError((cause) => new SqlError({
          cause,
          message: "Failed to execute statement"
        })));
      },
      export: Effect.try({
        try: () => sqlite3.serialize(db, "main"),
        catch: (cause) => new SqlError({
          cause,
          message: "Failed to export database"
        })
      }),
      import(data) {
        return Effect.try({
          try: () => sqlite3.deserialize(db, "main", data, data.length, data.length, 1 | 2),
          catch: (cause) => new SqlError({
            cause,
            message: "Failed to import database"
          })
        });
      }
    });
  });
  const semaphore = yield* Effect.makeSemaphore(1);
  const connection = yield* makeConnection;
  const acquirer = semaphore.withPermits(1)(Effect.succeed(connection));
  const transactionAcquirer = Effect.uninterruptibleMask((restore) => Effect.as(Effect.zipRight(restore(semaphore.take(1)), Effect.tap(Effect.scope, (scope2) => Scope.addFinalizer(scope2, semaphore.release(1)))), connection));
  return Object.assign(yield* Client.make({
    acquirer,
    compiler,
    transactionAcquirer,
    spanAttributes: [...options.spanAttributes ? Object.entries(options.spanAttributes) : [], [ATTR_DB_SYSTEM_NAME, "sqlite"]],
    transformRows
  }), {
    [TypeId]: TypeId,
    config: options,
    export: semaphore.withPermits(1)(connection.export),
    import(data) {
      return semaphore.withPermits(1)(connection.import(data));
    }
  });
});
var make4 = (options) => Effect.gen(function* () {
  const reactivity = yield* Reactivity.Reactivity;
  const compiler = Statement.makeCompilerSqlite(options.transformQueryNames);
  const transformRows = options.transformResultNames ? Statement.defaultTransforms(options.transformResultNames).array : void 0;
  const pending = /* @__PURE__ */ new Map();
  const makeConnection = Effect.gen(function* () {
    let currentId = 0;
    const scope2 = yield* Effect.scope;
    const readyDeferred = yield* Deferred.make();
    const worker = yield* options.worker;
    const port = "port" in worker ? worker.port : worker;
    const postMessage = (message, transferables) => port.postMessage(message, transferables);
    yield* Scope.addFinalizer(scope2, Effect.sync(() => postMessage(["close"])));
    const onMessage = (event) => {
      const [id, error, results] = event.data;
      if (id === "ready") {
        Deferred.unsafeDone(readyDeferred, Exit.void);
        return;
      } else if (id === "update_hook") {
        reactivity.unsafeInvalidate({
          [error]: [results]
        });
        return;
      } else {
        const resume = pending.get(id);
        if (!resume) return;
        pending.delete(id);
        if (error) {
          resume(Exit.fail(new SqlError({
            cause: error,
            message: "Failed to execute statement"
          })));
        } else {
          resume(Exit.succeed(results));
        }
      }
    };
    port.addEventListener("message", onMessage);
    function onError() {
      Effect.runFork(ScopedRef.set(connectionRef, makeConnection));
    }
    if ("onerror" in worker) {
      worker.addEventListener("error", onError);
    }
    yield* Scope.addFinalizer(scope2, Effect.sync(() => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
    }));
    yield* Deferred.await(readyDeferred);
    if (options.installReactivityHooks) {
      postMessage(["update_hook"]);
    }
    const send = (id, message, transferables) => Effect.async((resume) => {
      pending.set(id, resume);
      postMessage(message, transferables);
    });
    const run = (sql, params = [], rowMode = "object") => {
      const rows = Effect.withFiberRuntime((fiber) => {
        const id = currentId++;
        return send(id, [id, sql, params], fiber.getFiberRef(currentTransferables));
      });
      return rowMode === "object" ? Effect.map(rows, extractObject) : Effect.map(rows, extractRows);
    };
    return identity({
      execute(sql, params, transformRows2) {
        return transformRows2 ? Effect.map(run(sql, params), transformRows2) : run(sql, params);
      },
      executeRaw(sql, params) {
        return run(sql, params);
      },
      executeValues(sql, params) {
        return run(sql, params, "array");
      },
      executeUnprepared(sql, params, transformRows2) {
        return this.execute(sql, params, transformRows2);
      },
      executeStream() {
        return Effect.dieMessage("executeStream not implemented");
      },
      export: Effect.suspend(() => {
        const id = currentId++;
        return send(id, ["export", id]);
      }),
      import(data) {
        return Effect.suspend(() => {
          const id = currentId++;
          return send(id, ["import", id, data], [data.buffer]);
        });
      }
    });
  });
  const connectionRef = yield* ScopedRef.fromAcquire(makeConnection);
  const semaphore = yield* Effect.makeSemaphore(1);
  const acquirer = semaphore.withPermits(1)(ScopedRef.get(connectionRef));
  const transactionAcquirer = Effect.uninterruptibleMask((restore) => Effect.zipRight(Effect.zipRight(restore(semaphore.take(1)), Effect.tap(Effect.scope, (scope2) => Scope.addFinalizer(scope2, semaphore.release(1)))), ScopedRef.get(connectionRef)));
  return Object.assign(yield* Client.make({
    acquirer,
    compiler,
    transactionAcquirer,
    spanAttributes: [...options.spanAttributes ? Object.entries(options.spanAttributes) : [], [ATTR_DB_SYSTEM_NAME, "sqlite"]],
    transformRows
  }), {
    [TypeId]: TypeId,
    config: options,
    export: Effect.flatMap(acquirer, (connection) => connection.export),
    import(data) {
      return Effect.flatMap(acquirer, (connection) => connection.import(data));
    }
  });
});
function rowToObject(columns, row) {
  const obj = {};
  for (let i = 0; i < columns.length; i++) {
    obj[columns[i]] = row[i];
  }
  return obj;
}
var extractObject = (rows) => rows[1].map((row) => rowToObject(rows[0], row));
var extractRows = (rows) => rows[1];
var currentTransferables = /* @__PURE__ */ globalValue("@effect/sql-sqlite-wasm/currentTransferables", () => FiberRef.unsafeMake([]));
var withTransferables = (transferables) => (effect) => Effect.locally(effect, currentTransferables, transferables);
var layerMemoryConfig = (config) => Layer.scopedContext(Config.unwrap(config).pipe(Effect.flatMap(makeMemory), Effect.map((client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client))))).pipe(Layer.provide(Reactivity.layer));
var layerMemory = (config) => Layer.scopedContext(Effect.map(makeMemory(config), (client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client)))).pipe(Layer.provide(Reactivity.layer));
var layer2 = (config) => Layer.scopedContext(Effect.map(make4(config), (client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client)))).pipe(Layer.provide(Reactivity.layer));
var layerConfig = (config) => Layer.scopedContext(Config.unwrap(config).pipe(Effect.flatMap(make4), Effect.map((client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client))))).pipe(Layer.provide(Reactivity.layer));

export {
  TypeId,
  SqliteClient,
  makeMemory,
  make4 as make,
  currentTransferables,
  withTransferables,
  layerMemoryConfig,
  layerMemory,
  layer2 as layer,
  layerConfig
};
//# sourceMappingURL=chunk-C4KMPB4F.mjs.map
